// Arc Flash & Electrical Safety Awareness: mini-course page.
// Create React App + React Router v6, plain JavaScript.
//
// Route: /arc-flash   (views are selected with ?m=3, ?m=exam, ?m=certificate so the browser Back button works)
//
// Props (wire these to the app's existing access logic. See INTEGRATION.md):
//   hasAccess      boolean  true for active paid subscribers (hasFullAccess / isSubscribed)
//   session        object   Supabase session (used for the progress key and the certificate name)
//   loading        boolean  optional; show a loading state while subscription status is being checked
//   subscribePath  string   optional; where the "Subscribe to unlock" button goes (default "/pricing")
//   onProgress     function optional; called with the full progress object every time it changes,
//                           e.g. to save it to Supabase later. Progress is also kept in localStorage.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { COURSE, MODULES, FINAL_EXAM } from '../data/arcFlashData';
import './ArcFlash.css';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const EMPTY_PROGRESS = { modules: {}, exam: null, name: '' };

// ---------- helpers ----------
function shuffle(list) {
  const a = list.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function storageKey(session) {
  const id = session && session.user && session.user.id;
  return 'arcflash_progress_' + (id || 'guest');
}

function loadProgress(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return EMPTY_PROGRESS;
    const p = JSON.parse(raw);
    return { ...EMPTY_PROGRESS, ...p, modules: p.modules || {} };
  } catch (e) {
    return EMPTY_PROGRESS;
  }
}

function saveProgress(key, progress) {
  try {
    window.localStorage.setItem(key, JSON.stringify(progress));
  } catch (e) {
    /* storage unavailable: progress lives in memory for this visit */
  }
}

function makeCertId() {
  const part = () => Math.random().toString(36).slice(2, 6).toUpperCase();
  return 'AF-' + new Date().getFullYear() + '-' + part() + '-' + part();
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (e) {
    return iso;
  }
}

// ---------- quiz ----------
function Quiz({ questions, passMark, onFinish, title }) {
  const [attempt, setAttempt] = useState(0);
  const [answers, setAnswers] = useState({}); // question index -> option index (original)

  // Shuffle option order once per attempt
  const shuffled = useMemo(
    () => questions.map((q) => shuffle(q.options.map((text, k) => ({ text, k })))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, attempt]
  );

  const answeredCount = Object.keys(answers).length;
  const done = answeredCount === questions.length;
  const correct = questions.reduce((n, q, i) => n + (answers[i] === q.answer ? 1 : 0), 0);
  const passed = done && correct / questions.length >= passMark;

  useEffect(() => {
    if (done) onFinish(correct, questions.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const retake = () => {
    setAnswers({});
    setAttempt((a) => a + 1);
  };

  return (
    <div className="af-quiz">
      <h2>{title}</h2>
      <p className="af-quiz-intro">
        {questions.length} questions. You need {Math.ceil(passMark * questions.length)} correct ({Math.round(passMark * 100)}%) to pass.
      </p>
      {questions.map((q, i) => {
        const picked = answers[i];
        const isAnswered = picked !== undefined;
        return (
          <div className="af-q" key={attempt + '-' + i}>
            <p className="af-q-text">
              {i + 1}. {q.question}
            </p>
            {shuffled[i].map((opt, ix) => {
              let cls = 'af-opt';
              if (isAnswered && opt.k === q.answer) cls += ' af-right';
              if (isAnswered && opt.k === picked && picked !== q.answer) cls += ' af-wrong';
              return (
                <button
                  type="button"
                  key={opt.k}
                  className={cls}
                  disabled={isAnswered}
                  onClick={() => setAnswers((a) => ({ ...a, [i]: opt.k }))}
                >
                  <span className="af-letter">{LETTERS[ix]}</span>
                  <span>{opt.text}</span>
                </button>
              );
            })}
            {isAnswered && (
              <div className="af-exp">
                <strong>{picked === q.answer ? 'Correct. ' : 'Not quite. '}</strong>
                {q.explanation}
              </div>
            )}
          </div>
        );
      })}

      {!done && (
        <div className="af-quiz-progress">
          Answered {answeredCount} of {questions.length}
        </div>
      )}
      {done && (
        <div className={'af-score ' + (passed ? 'af-score-pass' : 'af-score-fail')}>
          <div className="af-score-num">
            {correct} / {questions.length}
          </div>
          <div>{passed ? '✓ Passed' : 'Not passed yet. Review the lesson and try again.'}</div>
          <button type="button" className="af-btn af-btn-ghost" onClick={retake}>
            Retake
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- certificate (opens a printable window) ----------
function printCertificate({ name, date, certId }) {
  const w = window.open('', '_blank', 'width=1100,height=800');
  if (!w) {
    alert('Please allow pop-ups to print your certificate.');
    return;
  }
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Certificate of Completion</title>
<link href="https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&family=Source+Sans+3:wght@400;600&display=swap" rel="stylesheet">
<style>
@page{size:landscape;margin:0}
body{margin:0;font-family:"Source Sans 3",Arial,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.c{width:10.5in;height:7.6in;margin:.2in auto;box-sizing:border-box;border:14px solid #0C1F3F;outline:3px solid #C9A84C;outline-offset:-26px;padding:.7in .8in;text-align:center;color:#0C1F3F;position:relative}
.k{font:600 15px Oswald,sans-serif;letter-spacing:5px;color:#C9A84C}
h1{font:700 44px Oswald,sans-serif;margin:10px 0 4px;letter-spacing:1px}
.n{font:600 38px Oswald,sans-serif;margin:26px 0 6px;border-bottom:2px solid #C9A84C;display:inline-block;padding:0 40px 6px}
.t{font-size:18px;margin:14px 0}
.ct{font:600 24px Oswald,sans-serif;color:#173468}
.d{display:flex;justify-content:space-between;margin-top:46px;font-size:15px}
.f{position:absolute;left:.8in;right:.8in;bottom:.55in;font-size:12.5px;color:#444;border-top:1px solid #ccc;padding-top:8px}
</style></head><body><div class="c">
<div class="k">CERTIFICATE OF COMPLETION</div>
<h1>${esc(COURSE.title)}</h1>
<div class="t">This certifies that</div>
<div class="n">${esc(name || ' ')}</div>
<div class="t">has completed all nine modules and passed the final exam of</div>
<div class="ct">${esc(COURSE.title)}: Awareness Level</div>
<div class="d"><div><strong>Date completed:</strong> ${esc(formatDate(date))}</div><div><strong>Certificate ID:</strong> ${esc(certId)}</div></div>
<div class="f">${esc(COURSE.certificateText)}</div>
</div></body></html>`);
  w.document.close();
  setTimeout(() => {
    w.focus();
    w.print();
  }, 700);
}

// ---------- main page ----------
export default function ArcFlash({ hasAccess, session, loading, subscribePath = '/subscribe', onProgress }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const rawView = params.get('m') || params.get('module');
  const view = rawView === 'final' ? 'exam' : rawView; // null = overview, '1'..'9', 'exam', 'certificate'
  const key = storageKey(session);
  const [progress, setProgress] = useState(() => loadProgress(key));

  useEffect(() => {
    setProgress(loadProgress(key));
  }, [key]);

  const update = useCallback(
    (fn) => {
      setProgress((prev) => {
        const next = fn(prev);
        saveProgress(key, next);
        if (onProgress) onProgress(next);
        return next;
      });
    },
    [key, onProgress]
  );

  // scroll to top when changing view
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const go = (m) => {
    const next = new URLSearchParams(params);
    if (m === null || m === undefined) {
      next.delete('m');
      next.delete('module');
    } else {
      next.set('m', String(m));
      next.delete('module');
    }
    setParams(next, { replace: false });
  };

  const passedCount = MODULES.filter((m) => progress.modules[m.id] && progress.modules[m.id].passed).length;
  const allPassed = passedCount === MODULES.length;
  const examPassed = !!(progress.exam && progress.exam.passed);
  const defaultName = '';
  const exitToQuiz = useCallback(() => {
    navigate('/quiz', { replace: false });
  }, [navigate]);

  if (loading) {
    return (
      <div className="af">
        <div className="af-wrap">
          <p className="af-muted">Loading…</p>
        </div>
      </div>
    );
  }

  // ----- locked (not a subscriber) -----
  if (!hasAccess) {
    return (
      <div className="af">
        <Banner kicker="Subscriber course" title={COURSE.title} sub={COURSE.subtitle} />
        <div className="af-wrap">
          <p className="af-lead">{COURSE.description}</p>
          <div className="af-locked" style={{ display: 'grid', gap: '0.75rem', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div className="af-lock-icon" aria-hidden="true">🔒</div>
              <div>
                <strong>This course is included with a paid subscription.</strong>
                <div className="af-muted">Subscribe to unlock all nine modules, quizzes, the final exam and your certificate.</div>
              </div>
            </div>
            <Link className="af-btn" to={subscribePath}>
              Subscribe to unlock
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', margin: '0.15rem 0' }}>
              <span style={{ flex: 1, height: '1px', background: 'rgba(17, 24, 39, 0.38)' }} />
              <span style={{ color: '#111827', fontSize: '0.74rem', fontWeight: 800, lineHeight: 1, letterSpacing: '0.12em', textTransform: 'lowercase' }}>or</span>
              <span style={{ flex: 1, height: '1px', background: 'rgba(17, 24, 39, 0.38)' }} />
            </div>
            <button
              type="button"
              className="af-btn"
              onClick={exitToQuiz}
              style={{ background: '#1d8f5d', color: '#fff', border: '1px solid rgba(255,255,255,0.18)', width: '100%' }}
            >
              Back to Home
            </button>
          </div>
          <h2 className="af-h2">What's inside</h2>
          <ol className="af-simple-list">
            {MODULES.map((m) => (
              <li key={m.id}>
                <strong>{m.title}</strong> <span className="af-muted">· {m.minutes} min</span>
              </li>
            ))}
            <li>
              <strong>Final exam</strong> <span className="af-muted">· {FINAL_EXAM.length} questions, then a certificate of completion</span>
            </li>
          </ol>
        </div>
      </div>
    );
  }

  // ----- single module -----
  const mod = MODULES.find((m) => String(m.id) === view);
  if (mod) {
    const idx = MODULES.indexOf(mod);
    const prev = MODULES[idx - 1];
    const next = MODULES[idx + 1];
    const rec = progress.modules[mod.id];
    return (
      <div className="af">
        <Banner
          kicker={`Module ${mod.id} of ${MODULES.length}`}
          title={mod.title}
          sub={`${mod.minutes} min`}
          back={() => go(null)}
          status={rec && rec.passed ? `✓ Passed · best ${rec.best}/${rec.total}` : null}
        />
        <div className="af-wrap">
          <div className="af-notice">
            <strong>Awareness training only.</strong> This course explains electrical hazards in general terms. It does not qualify
            you to work on or near energized equipment and does not replace CSA Z462, your employer's electrical safety program, or
            task-specific training.
          </div>

          <article className="af-lesson" dangerouslySetInnerHTML={{ __html: mod.html }} />

          <Quiz
            key={mod.id}
            title={`Module ${mod.id} quiz`}
            questions={mod.quiz}
            passMark={COURSE.passMark}
            onFinish={(score, total) =>
              update((p) => {
                const old = p.modules[mod.id] || { best: 0 };
                const passed = (old.passed || false) || score / total >= COURSE.passMark;
                return {
                  ...p,
                  modules: {
                    ...p.modules,
                    [mod.id]: { best: Math.max(old.best || 0, score), total, passed, updatedAt: new Date().toISOString() },
                  },
                };
              })
            }
          />

          <div className="af-pager">
            {prev ? (
              <button type="button" className="af-btn af-btn-ghost" onClick={() => go(prev.id)}>
                ‹ Module {prev.id}
              </button>
            ) : (
              <button type="button" className="af-btn af-btn-ghost" onClick={() => go(null)}>
                ‹ All modules
              </button>
            )}
            {next ? (
              <button type="button" className="af-btn" onClick={() => go(next.id)}>
                Next: Module {next.id} ›
              </button>
            ) : (
              <button type="button" className="af-btn" onClick={() => go(allPassed ? 'exam' : null)}>
                {allPassed ? 'Go to final exam ›' : 'Back to all modules'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ----- final exam -----
  if (view === 'exam') {
    return (
      <div className="af">
        <Banner kicker="Final exam" title="Final Exam" back={() => go(null)} />
        <div className="af-wrap">
          {!allPassed ? (
            <div className="af-locked">
              <div className="af-lock-icon" aria-hidden="true">🔒</div>
              <div>
                <strong>Pass all nine module quizzes to unlock the final exam.</strong>
                <div className="af-muted">
                  You've passed {passedCount} of {MODULES.length}.
                </div>
              </div>
              <button type="button" className="af-btn" onClick={() => go(null)}>
                Back to modules
              </button>
            </div>
          ) : (
            <>
              <p className="af-lead">
                {FINAL_EXAM.length} questions covering all nine modules. Pass with {Math.round(COURSE.passMark * 100)}% to earn your
                certificate of completion.
              </p>
              <Quiz
                title="Final exam"
                questions={FINAL_EXAM}
                passMark={COURSE.passMark}
                onFinish={(score, total) =>
                  update((p) => {
                    const old = p.exam || { best: 0 };
                    const passedNow = score / total >= COURSE.passMark;
                    const passed = old.passed || passedNow;
                    return {
                      ...p,
                      exam: {
                        best: Math.max(old.best || 0, score),
                        total,
                        passed,
                        certId: old.certId || (passed ? makeCertId() : undefined),
                        completedAt: old.completedAt || (passed ? new Date().toISOString() : undefined),
                      },
                    };
                  })
                }
              />
              {examPassed && (
                <div className="af-pager">
                  <span />
                  <button type="button" className="af-btn" onClick={() => go('certificate')}>
                    Get your certificate ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ----- certificate / completion -----
  if (view === 'certificate') {
    const name = progress.name || defaultName;
    return (
      <div className="af">
        <Banner kicker="Course complete" title="Certificate of Completion" back={() => go(null)} />
        <div className="af-wrap">
          {!examPassed ? (
            <div className="af-locked">
              <div className="af-lock-icon" aria-hidden="true">🔒</div>
              <div>
                <strong>Pass the final exam to receive your certificate.</strong>
              </div>
              <button type="button" className="af-btn" onClick={() => go(allPassed ? 'exam' : null)}>
                {allPassed ? 'Go to final exam' : 'Back to modules'}
              </button>
            </div>
          ) : (
            <>
              <div className="af-complete">
                <div className="af-complete-check">✓</div>
                <h2>You've completed the course</h2>
                <p>
                  All nine modules passed and final exam score {progress.exam.best}/{progress.exam.total}.
                </p>
              </div>
              <label className="af-label" htmlFor="af-name">
                Name as it should appear on your certificate
              </label>
              <input
                id="af-name"
                className="af-input"
                value={name}
                placeholder="Your full name"
                onChange={(e) => {
                  const v = e.target.value;
                  update((p) => ({ ...p, name: v }));
                }}
              />
              <div className="af-cert-preview">
                <div className="af-cert-kicker">CERTIFICATE OF COMPLETION</div>
                <div className="af-cert-title">{COURSE.title}</div>
                <div className="af-cert-name">{name || ''}</div>
                <div className="af-cert-meta">
                  Completed {formatDate(progress.exam.completedAt)} · ID {progress.exam.certId}
                </div>
                <div className="af-cert-fine">{COURSE.certificateText}</div>
              </div>
              <button
                type="button"
                className="af-btn"
                disabled={!name.trim()}
                onClick={() => printCertificate({ name, date: progress.exam.completedAt, certId: progress.exam.certId })}
              >
                Print / save as PDF
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ----- overview (module cards) -----
  const nextUp = MODULES.find((m) => !(progress.modules[m.id] && progress.modules[m.id].passed));
  const pct = Math.round((passedCount / MODULES.length) * 100);
  return (
    <div className="af">
      <Banner kicker="Awareness course · CSA Z462:24" title={COURSE.title} sub={COURSE.subtitle} />
      <div className="af-wrap">
        <p className="af-lead">{COURSE.description}</p>

        <div className="af-progress-card">
          <div className="af-progress-top">
            <strong>
              {passedCount} of {MODULES.length} modules passed
            </strong>
            <span>{pct}%</span>
          </div>
          <div className="af-bar">
            <div className="af-bar-fill" style={{ width: pct + '%' }} />
          </div>
          <div className="af-progress-actions">
            {examPassed ? (
              <button type="button" className="af-btn" onClick={() => go('certificate')}>
                View certificate
              </button>
            ) : nextUp ? (
              <button type="button" className="af-btn" onClick={() => go(nextUp.id)}>
                {passedCount === 0 ? 'Start Module 1' : `Continue: Module ${nextUp.id}`}
              </button>
            ) : (
              <button type="button" className="af-btn" onClick={() => go('exam')}>
                Take the final exam
              </button>
            )}
          </div>
        </div>

        <div className="af-grid">
          {MODULES.map((m) => {
            const rec = progress.modules[m.id];
            const status = rec ? (rec.passed ? 'passed' : 'tried') : 'new';
            return (
              <button type="button" key={m.id} className={'af-card af-card-' + status} onClick={() => go(m.id)}>
                <div className="af-card-num">{m.id}</div>
                <div className="af-card-body">
                  <div className="af-card-title">{m.title}</div>
                  <div className="af-card-meta">
                    {m.minutes} min · {m.quiz.length}-question quiz
                  </div>
                  <div className={'af-chip af-chip-' + status}>
                    {status === 'passed'
                      ? `✓ Passed · ${rec.best}/${rec.total}`
                      : status === 'tried'
                      ? `Best ${rec.best}/${rec.total} · not passed`
                      : 'Not started'}
                  </div>
                </div>
              </button>
            );
          })}

          <button
            type="button"
            className={'af-card af-card-exam' + (allPassed ? '' : ' af-card-locked')}
            onClick={() => go(examPassed ? 'certificate' : 'exam')}
          >
            <div className="af-card-num">{allPassed ? '★' : '🔒'}</div>
            <div className="af-card-body">
              <div className="af-card-title">Final exam and certificate</div>
              <div className="af-card-meta">{FINAL_EXAM.length} questions · 80% to pass</div>
              <div className={'af-chip ' + (examPassed ? 'af-chip-passed' : 'af-chip-new')}>
                {examPassed
                  ? `✓ Passed · ${progress.exam.best}/${progress.exam.total}`
                  : allPassed
                  ? 'Unlocked'
                  : 'Unlocks when all modules are passed'}
              </div>
            </div>
          </button>
        </div>

        <details className="af-details">
          <summary>About this course and disclaimer</summary>
          <div className="af-lesson" dangerouslySetInnerHTML={{ __html: COURSE.disclaimerHtml }} />
        </details>
      </div>
    </div>
  );
}

function Banner({ kicker, title, sub, back, status }) {
  const showTimeInKicker = typeof sub === 'string' && /\d+\s*min/i.test(sub);
  const kickerText = showTimeInKicker ? `${kicker} · ${sub.toUpperCase()}` : kicker;

  return (
    <div className="af-banner">
      <div className="af-wrap af-banner-inner">
        <div className="af-banner-topline">
          <div className="af-kicker">{kickerText}</div>
        </div>
        <h1>{title}</h1>
        {!showTimeInKicker && sub && <div className="af-sub">{sub}</div>}
        {status && <div className="af-status">{status}</div>}
      </div>
    </div>
  );
}
